// The one money formatter (v1.0.2 §3.3). Conventions, shared with chart axes:
//   - the sign goes before the `$`, and negatives use a true minus (U+2212), which is as wide as
//     `+` in tabular figures, so signed columns line up;
//   - a value that rounds to zero is an unsigned zero, never "−$0.00";
//   - anything that isn't a finite number renders as PLACEHOLDER.
// Digits get their lining figures from CSS (index.css body + liningFigures.test.js), not from here.

export const PLACEHOLDER = '—';
const MINUS = '−';

function toNumber(value) {
  if (typeof value === 'string') return value.trim() === '' ? NaN : Number(value);
  return typeof value === 'number' ? value : NaN;
}

function withSign(magnitude, rounded, sign) {
  if (rounded === 0 || sign === 'never') return magnitude;
  if (rounded < 0) return MINUS + magnitude;
  return sign === 'always' ? `+${magnitude}` : magnitude;
}

/**
 * @param {number|string|null|undefined} value
 * @param {{ decimals?: number, sign?: 'auto'|'always'|'never' }} [options]
 *   sign: 'auto' marks only negatives, 'always' adds + to gains, 'never' prints the magnitude.
 * @returns {string} e.g. "$88,674.32", "−$5.00", "+$2,602.80"
 */
export function formatCurrency(value, { decimals = 2, sign = 'auto' } = {}) {
  const n = toNumber(value);
  if (!Number.isFinite(n)) return PLACEHOLDER;
  const rounded = Number(n.toFixed(decimals));
  const body = Math.abs(rounded).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  return withSign(`$${body}`, rounded, sign);
}

/**
 * Percentages, with the same sign and placeholder rules as money: "3.02%", "−3.02%", "+3.02%".
 * @param {number|string|null|undefined} value already in percent units (3.02, not 0.0302)
 * @param {{ decimals?: number, sign?: 'auto'|'always'|'never' }} [options]
 */
export function formatPercent(value, { decimals = 2, sign = 'auto' } = {}) {
  const n = toNumber(value);
  if (!Number.isFinite(n)) return PLACEHOLDER;
  const rounded = Number(n.toFixed(decimals));
  const body = Math.abs(rounded).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  return withSign(`${body}%`, rounded, sign);
}

const UNITS = [
  { min: 1e12, divisor: 1e12, suffix: 'T' },
  { min: 1e9, divisor: 1e9, suffix: 'B' },
  { min: 1e6, divisor: 1e6, suffix: 'M' },
  { min: 1e3, divisor: 1e3, suffix: 'k' },
  { min: 0, divisor: 1, suffix: '' },
];

function smallestStep(ticks) {
  const sorted = [...new Set(ticks)].sort((a, b) => a - b);
  let step = Infinity;
  for (let i = 1; i < sorted.length; i += 1) step = Math.min(step, sorted[i] - sorted[i - 1]);
  return Number.isFinite(step) && step > 0 ? step : null;
}

/**
 * Compact labels ("$1.5k", "−$2M", "$2.500T"). Pass a chart axis's `ticks` and the decimals follow
 * the tick step, so adjacent labels never collapse into the same text, and the unit is chosen once
 * for the whole axis from its largest tick. `decimals` overrides both rules.
 * @param {number|string|null|undefined} value
 * @param {{ ticks?: number[], decimals?: number, sign?: 'auto'|'always'|'never' }} [options]
 */
export function formatCompactCurrency(value, { ticks, decimals: fixedDecimals, sign = 'auto' } = {}) {
  const n = toNumber(value);
  if (!Number.isFinite(n)) return PLACEHOLDER;
  const finiteTicks = (ticks || []).filter(Number.isFinite);
  const scale = finiteTicks.length ? Math.max(...finiteTicks.map(Math.abs)) : Math.abs(n);
  const unit = UNITS.find(u => scale >= u.min);
  const step = finiteTicks.length > 1 ? smallestStep(finiteTicks) : null;
  // 1e-9 keeps an exact power of ten (step 1 → 0 decimals) from rounding up a place.
  let decimals = unit.suffix ? 1 : 0;
  if (Number.isInteger(fixedDecimals)) decimals = fixedDecimals;
  else if (step) decimals = Math.max(0, Math.ceil(-Math.log10(step / unit.divisor) - 1e-9));
  const rounded = Number((n / unit.divisor).toFixed(decimals));
  const body = Math.abs(rounded).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  return withSign(`$${body}${unit.suffix}`, rounded, sign);
}
